import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LearnerAppConfigResponse, LearnerAppConfigUpdateRequest } from "@eduviaos/shared";
import { api } from "@/lib/api";

export const learnerAppConfigKeys = {
  all: ["learner-app-config"] as const,
  tenant: (slug: string) => [...learnerAppConfigKeys.all, slug] as const,
};

export function useLearnerAppConfig(tenantSlug: string, enabled = true) {
  return useQuery<LearnerAppConfigResponse>({
    queryKey: learnerAppConfigKeys.tenant(tenantSlug),
    queryFn: () =>
      api.get<LearnerAppConfigResponse>(`/super-admin/tenants/${tenantSlug}/learner-app-config`),
    enabled: Boolean(tenantSlug) && enabled,
    staleTime: 30_000,
  });
}

export function useUpdateLearnerAppConfig(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation<LearnerAppConfigResponse, Error, LearnerAppConfigUpdateRequest>({
    mutationFn: (body) =>
      api.put<LearnerAppConfigResponse>(
        `/super-admin/tenants/${tenantSlug}/learner-app-config`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learnerAppConfigKeys.tenant(tenantSlug) });
      void qc.invalidateQueries({ queryKey: ["super-admin", "tenants"] });
    },
  });
}

/** Display paise as INR without floating-point drift. */
export function formatLearnerAppPrice(paise: number): string {
  const rupees = paise / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
  }).format(rupees);
}
